import { c as _c } from "react/compiler-runtime";
// Location picker. A pill that opens a modal to choose country, city and
// display currency. Countries (with their cities + default currency) and
// currencies are served by the gateway (`api.geo.*`). The site is Russian-only,
// so there is no language option here — the locale is pinned in `src/i18n`.
//
// По макету (Айталы A-05):
//   • первый заход — под городом в шапке вопрос «Ваш город Москва?» с кнопками
//     «Сменить город» / «Да, оставить» (кадр 3956:114950);
//   • «Сменить город» — окно «Выбор города»: поиск, список, «Выбрать»
//     (кадр 3956:116171);
//   • клик по городу в шапке — окно «Регион и валюта»: состав прежний (страна,
//     город, валюта), город выбирается тем же поиском со списком, кнопка
//     «Применить» тёмная, как «Выбрать» в макете.
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useState, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/api";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@osmix/ui/dialog";
import { ChevronDown20Icon, MarkerPinIcon, Search20Icon, XIcon } from "@/components/icons";
import { useCityPrompt } from "@/components/layout/city-prompt";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/text-field";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { saveCityToServer } from "@/lib/use-region";
import { usePreferences, useProfile } from "@/store";
type Mode = "region" | "city";

// В шапке два экземпляра кнопки (десктоп ≥ 1024 и планшет/телефон), видим
// один. Вопрос о городе привязан к видимому — иначе поповер встал бы у
// скрытой кнопки в угол экрана.
function useMedia(query) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== query) {
    t0 = cb => {
      if (!query || typeof window === "undefined") {
        return _temp;
      }
      const m = window.matchMedia(query);
      m.addEventListener("change", cb);
      return () => m.removeEventListener("change", cb);
    };
    t1 = () => query && typeof window !== "undefined" ? window.matchMedia(query).matches : true;
    $[0] = query;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  return useSyncExternalStore(t0, t1, _temp2);
}

// Селектор города в шапке по макету «Баннер»: просто «{город} ⌄» без рамки и
// валюты; над тёмным hero — белый, иначе тёмный. Клик открывает модалку выбора
// страны/города/валюты. `onDark` синхронизирует цвет с остальной шапкой.
// `media` — при какой ширине этот экземпляр виден (для вопроса о городе).
// `variant="icon"` — телефонная шапка (кадр «Mobile» 3840:145854): вместо
// «{город} ⌄» значок метки 24 в кнопке 36×40.
function _temp2() {
  return false;
}
function _temp() {}
export function LocationButton(t0) {
  const $ = _c(54);
  const {
    className,
    onDark: t1,
    media,
    variant: t2
  } = t0;
  const onDark = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "text" : t2;
  const {
    t
  } = useTranslation();
  const city = usePreferences(_temp3);
  const geoPrompted = usePreferences(_temp4);
  const profileCity = useProfile(_temp5);
  const {
    confirmed,
    dismissed,
    confirm,
    dismiss
  } = useCityPrompt();
  const visible = useMedia(media);
  const [mode, setMode] = useState(null);
  const ask = visible && geoPrompted && !confirmed && !dismissed && !profileCity && mode === null;
  const t3 = mode !== null;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = open => setMode(open ? "region" : null);
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  let t5;
  if ($[1] !== dismiss) {
    t5 = open_0 => {
      if (!open_0) {
        dismiss();
      }
    };
    $[1] = dismiss;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  let t6;
  if ($[3] !== city || $[4] !== t || $[5] !== variant) {
    t6 = variant === "icon" ? `${t("location.title", {
      defaultValue: "\u0420\u0435\u0433\u0438\u043E\u043D"
    })}: ${city}` : t("location.title", {
      defaultValue: "\u0420\u0435\u0433\u0438\u043E\u043D"
    });
    $[3] = city;
    $[4] = t;
    $[5] = variant;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = ask || undefined;
  const t8 = variant === "icon" && "h-10 w-9 shrink-0 justify-center";
  const t9 = onDark ? "text-white/96 hover:text-white/80" : "text-[#1F1F1F] hover:text-osmix-tangelo";
  let t10;
  if ($[7] !== className || $[8] !== t8 || $[9] !== t9) {
    t10 = cn("group inline-flex items-center gap-1 whitespace-nowrap text-[16px] font-semibold leading-5 tracking-[-0.31px] outline-none transition-colors focus-visible:text-osmix-tangelo", t8, t9, className);
    $[7] = className;
    $[8] = t8;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== city || $[12] !== variant) {
    t11 = variant === "icon" ? <MarkerPinIcon size={24} /> : <><span>{city}</span><ChevronDown20Icon size={20} className="shrink-0 transition-transform group-data-[asking]:rotate-180 group-data-[state=open]:rotate-180" /></>;
    $[11] = city;
    $[12] = variant;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t10 || $[15] !== t11 || $[16] !== t6 || $[17] !== t7) {
    t12 = <PopoverAnchor asChild={true}><DialogTrigger aria-label={t6} data-asking={t7} className={t10}>{t11}</DialogTrigger></PopoverAnchor>;
    $[14] = t10;
    $[15] = t11;
    $[16] = t6;
    $[17] = t7;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== city || $[20] !== t) {
    t13 = t("location.askCity", {
      city,
      defaultValue: "\u0412\u0430\u0448 \u0433\u043E\u0440\u043E\u0434 {{city}}?"
    });
    $[19] = city;
    $[20] = t;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t13) {
    t14 = <p className="px-3 py-2.5 text-[14px] leading-[19px] font-medium text-[#1F1F1F]">{t13}</p>;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => setMode("city");
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t) {
    t16 = t("location.changeCity", {
      defaultValue: "\u0421\u043C\u0435\u043D\u0438\u0442\u044C \u0433\u043E\u0440\u043E\u0434"
    });
    $[25] = t;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== t16) {
    t17 = <Button variant="ghost" size="md" className="flex-1" onClick={t15}>{t16}</Button>;
    $[27] = t16;
    $[28] = t17;
  } else {
    t17 = $[28];
  }
  let t18;
  if ($[29] !== t) {
    t18 = t("location.keepCity", {
      defaultValue: "\u0414\u0430, \u043E\u0441\u0442\u0430\u0432\u0438\u0442\u044C"
    });
    $[29] = t;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== confirm || $[32] !== t18) {
    t19 = <Button size="md" className="bg-ds-ink-2 hover:bg-ds-primary" onClick={confirm}>{t18}</Button>;
    $[31] = confirm;
    $[32] = t18;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t17 || $[35] !== t19) {
    t20 = <div className="flex w-full gap-2.5">{t17}{t19}</div>;
    $[34] = t17;
    $[35] = t19;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t14 || $[38] !== t20) {
    t21 = <PopoverContent align="center" sideOffset={10} collisionPadding={16} onOpenAutoFocus={_temp6} className="flex w-[291px] flex-col items-center gap-2.5 rounded-[10px] border-0 bg-white p-3 shadow-[0_10px_20px_rgba(0,0,0,0.1)]" data-testid="city-prompt">{t14}{t20}</PopoverContent>;
    $[37] = t14;
    $[38] = t20;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== ask || $[41] !== t12 || $[42] !== t21 || $[43] !== t5) {
    t22 = <Popover open={ask} onOpenChange={t5}>{t12}{t21}</Popover>;
    $[40] = ask;
    $[41] = t12;
    $[42] = t21;
    $[43] = t5;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== confirm || $[46] !== mode) {
    t23 = mode && <LocationForm mode={mode} onDone={() => {
      confirm();
      setMode(null);
    }} />;
    $[45] = confirm;
    $[46] = mode;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  let t24;
  if ($[48] !== t23) {
    t24 = <DialogContent showCloseButton={false} className="w-full max-w-[calc(100%-32px)] gap-0 overflow-hidden rounded-[20px] border-0 bg-white p-0 sm:max-w-[440px]" overlayClassName="bg-black/20" onCloseAutoFocus={_temp7}>{t23}</DialogContent>;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== t22 || $[51] !== t24 || $[52] !== t3) {
    t25 = <Dialog open={t3} onOpenChange={t4}>{t22}{t24}</Dialog>;
    $[50] = t22;
    $[51] = t24;
    $[52] = t3;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  return t25;
}
function _temp7(e_0) {
  return e_0.preventDefault();
}
function _temp6(e) {
  return e.preventDefault();
}
function _temp5(s_1) {
  return s_1.profile?.city;
}
function _temp4(s_0) {
  return s_0.geoPrompted;
}
function _temp3(s) {
  return s.city;
}
function LocationForm(t0) {
  const $ = _c(70);
  const {
    mode,
    onDone
  } = t0;
  const {
    t
  } = useTranslation();
  const prefs = usePreferences();
  const setLocation = usePreferences(_temp8);
  const setCurrency = usePreferences(_temp9);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["geo", "countries"],
      queryFn: _temp0
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const countries = useQuery(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      queryKey: ["geo", "currencies"],
      queryFn: _temp1
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const currencies = useQuery(t2);
  const [countryCode, setCountryCode] = useState(prefs.countryCode);
  const [city, setCity] = useState(prefs.city);
  const [currencyCode, setCurrencyCode] = useState(prefs.currency.code);
  let t3;
  if ($[2] !== countries.data || $[3] !== countryCode) {
    t3 = countries.data?.find(c => c.code === countryCode)?.cities ?? [];
    $[2] = countries.data;
    $[3] = countryCode;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const cities = t3;
  const loading = countries.isPending || currencies.isPending;
  let t4;
  if ($[5] !== countries.data) {
    t4 = code => {
      setCountryCode(code);
      const c_0 = countries.data?.find(x => x.code === code);
      if (c_0) {
        setCity(c_0.cities[0] ?? "");
        setCurrencyCode(c_0.currency);
      }
    };
    $[5] = countries.data;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const onCountryChange = t4;
  let t5;
  if ($[7] !== city || $[8] !== countries.data || $[9] !== countryCode || $[10] !== currencies.data || $[11] !== currencyCode || $[12] !== mode || $[13] !== onDone || $[14] !== setCurrency || $[15] !== setLocation) {
    t5 = () => {
      const country = countries.data?.find(c_1 => c_1.code === countryCode);
      const currency = currencies.data?.find(c_2 => c_2.code === currencyCode);
      if (country) {
        setLocation({
          code: country.code,
          name: country.name
        }, city);
        saveCityToServer(city, country.code);
      }
      if (mode === "region" && currency) {
        setCurrency(currency);
      }
      onDone();
    };
    $[7] = city;
    $[8] = countries.data;
    $[9] = countryCode;
    $[10] = currencies.data;
    $[11] = currencyCode;
    $[12] = mode;
    $[13] = onDone;
    $[14] = setCurrency;
    $[15] = setLocation;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const apply = t5;
  let t6;
  if ($[17] !== mode || $[18] !== t) {
    t6 = mode === "city" ? t("location.cityTitle", {
      defaultValue: "\u0412\u044B\u0431\u043E\u0440 \u0433\u043E\u0440\u043E\u0434\u0430"
    }) : t("location.title");
    $[17] = mode;
    $[18] = t;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const title = t6;
  let t7;
  if ($[20] !== mode || $[21] !== t) {
    t7 = mode === "city" ? t("location.citySubtitle", {
      defaultValue: "\u0410\u0441\u0441\u043E\u0440\u0442\u0438\u043C\u0435\u043D\u0442 \u0442\u043E\u0432\u0430\u0440\u043E\u0432 \u0437\u0430\u0432\u0438\u0441\u0438\u0442 \u043E\u0442 \u0432\u044B\u0431\u0440\u0430\u043D\u043D\u043E\u0433\u043E \u0433\u043E\u0440\u043E\u0434\u0430."
    }) : t("location.subtitle");
    $[20] = mode;
    $[21] = t;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const subtitle = t7;
  let t8;
  if ($[23] !== title) {
    t8 = <DialogTitle className="min-w-0 flex-1 text-[20px] leading-[23px] font-semibold tracking-[-0.31px] text-[#161616]">{title}</DialogTitle>;
    $[23] = title;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t) {
    t9 = t("common.close", {
      defaultValue: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C"
    });
    $[25] = t;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <XIcon size={24} />;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t9) {
    t11 = <DialogClose aria-label={t9} className="grid size-6 shrink-0 place-items-center rounded-full text-ds-primary outline-none hover:bg-black/5 focus-visible:ring-2 focus-visible:ring-ds-focus">{t10}</DialogClose>;
    $[28] = t9;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11 || $[31] !== t8) {
    t12 = <div className="flex shrink-0 items-start gap-2.5 px-4 py-2.5">{t8}{t11}</div>;
    $[30] = t11;
    $[31] = t8;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== subtitle) {
    t13 = <DialogDescription className="text-[14px] leading-[18px] font-medium text-[#2E2E2E]">{subtitle}</DialogDescription>;
    $[33] = subtitle;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== countries.data || $[36] !== countryCode || $[37] !== loading || $[38] !== mode || $[39] !== onCountryChange || $[40] !== t) {
    t14 = mode === "region" && <Field label={t("location.country")}><Select value={countryCode} onValueChange={onCountryChange} disabled={loading}><SelectTrigger className="w-full"><SelectValue placeholder={t("location.country")} /></SelectTrigger><SelectContent>{(countries.data ?? []).map(_temp10)}</SelectContent></Select></Field>;
    $[35] = countries.data;
    $[36] = countryCode;
    $[37] = loading;
    $[38] = mode;
    $[39] = onCountryChange;
    $[40] = t;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== cities || $[43] !== city || $[44] !== loading || $[45] !== mode || $[46] !== t) {
    t15 = mode === "region" ? <Field label={t("location.city")}><CityPicker cities={cities} value={city} onChange={setCity} loading={loading} listClassName="max-h-[195px]" /><span className="text-[13px] leading-[18px] text-[#333]">{t("location.cityHint")}</span></Field> : <CityPicker cities={cities} value={city} onChange={setCity} loading={loading} listClassName="max-h-[386px]" />;
    $[42] = cities;
    $[43] = city;
    $[44] = loading;
    $[45] = mode;
    $[46] = t;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== currencies.data || $[49] !== currencyCode || $[50] !== loading || $[51] !== mode || $[52] !== t) {
    t16 = mode === "region" && <Field label={t("location.currency")}><Select value={currencyCode} onValueChange={setCurrencyCode} disabled={loading}><SelectTrigger className="w-full"><SelectValue placeholder={t("location.currency")} /></SelectTrigger><SelectContent>{(currencies.data ?? []).map(_temp11)}</SelectContent></Select></Field>;
    $[48] = currencies.data;
    $[49] = currencyCode;
    $[50] = loading;
    $[51] = mode;
    $[52] = t;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  let t17;
  if ($[54] !== t13 || $[55] !== t14 || $[56] !== t15 || $[57] !== t16) {
    t17 = <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-4 py-2.5">{t13}{t14}{t15}{t16}</div>;
    $[54] = t13;
    $[55] = t14;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  const t18 = loading || !city;
  let t19;
  if ($[59] !== mode || $[60] !== t) {
    t19 = mode === "city" ? t("location.choose", {
      defaultValue: "\u0412\u044B\u0431\u0440\u0430\u0442\u044C"
    }) : t("location.apply");
    $[59] = mode;
    $[60] = t;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== apply || $[63] !== t18 || $[64] !== t19) {
    t20 = <div className="flex shrink-0 justify-end px-4 pt-2.5 pb-4"><Button onClick={apply} disabled={t18} data-testid="location-apply" className="bg-ds-ink-2 hover:bg-ds-primary">{t19}</Button></div>;
    $[62] = apply;
    $[63] = t18;
    $[64] = t19;
    $[65] = t20;
  } else {
    t20 = $[65];
  }
  let t21;
  if ($[66] !== t12 || $[67] !== t17 || $[68] !== t20) {
    t21 = <div className="flex max-h-[calc(100dvh-32px)] flex-col">{t12}{t17}{t20}</div>;
    $[66] = t12;
    $[67] = t17;
    $[68] = t20;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  return t21;
}

/*
 * Город — поиском со списком (кадр 3956:116171): поле «Найти город» 42, r100,
 * #F8F8F8, pad 8, иконка 20; пункты 39, r8, pad 10/12, Manrope 14/19 w500
 * #1F1F1F; выбранный — подложка #1F1F1F 4 %.
 */
function _temp11(c_4) {
  return <SelectItem key={c_4.code} value={c_4.code}><span className="flex items-center gap-2"><span className="w-5 text-center text-osmix-text-2">{c_4.symbol}</span>{c_4.code} — {c_4.name}</span></SelectItem>;
}
function _temp10(c_3) {
  return <SelectItem key={c_3.code} value={c_3.code}>{c_3.name}</SelectItem>;
}
function _temp1() {
  return api.geo.currencies();
}
function _temp0() {
  return api.geo.countries();
}
function _temp9(s_0) {
  return s_0.setCurrency;
}
function _temp8(s) {
  return s.setLocation;
}
function CityPicker(t0) {
  const $ = _c(30);
  const {
    cities,
    value,
    onChange,
    loading,
    listClassName
  } = t0;
  const {
    t
  } = useTranslation();
  const [query, setQuery] = useState("");
  let t1;
  if ($[0] !== cities || $[1] !== query) {
    const q = query.trim().toLowerCase();
    t1 = q ? cities.filter(c => c.toLowerCase().includes(q)) : cities;
    $[0] = cities;
    $[1] = query;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const shown = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = e => setQuery(e.target.value);
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("location.citySearch", {
      defaultValue: "\u041D\u0430\u0439\u0442\u0438 \u0433\u043E\u0440\u043E\u0434"
    });
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("location.citySearch", {
      defaultValue: "\u041D\u0430\u0439\u0442\u0438 \u0433\u043E\u0440\u043E\u0434"
    });
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Search20Icon size={20} className="text-[#A4A4A4]" />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== query || $[10] !== t3 || $[11] !== t4) {
    t6 = <Input type="search" value={query} onChange={t2} placeholder={t3} aria-label={t4} start={t5} fieldClassName="h-[42px] gap-2.5 rounded-full bg-[#F8F8F8] px-2" className="tracking-[-0.42px] placeholder:text-[#A4A4A4]" />;
    $[9] = query;
    $[10] = t3;
    $[11] = t4;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t) {
    t7 = t("location.city");
    $[13] = t;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== listClassName) {
    t8 = cn("flex flex-col overflow-y-auto", listClassName);
    $[15] = listClassName;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== loading || $[18] !== onChange || $[19] !== shown || $[20] !== t || $[21] !== value) {
    t9 = loading ? <span className="px-3 py-2.5 text-[14px] leading-[19px] text-[#A4A4A4]">…</span> : shown.length === 0 ? <span className="px-3 py-2.5 text-[14px] leading-[19px] text-[#A4A4A4]">{t("location.cityNotFound", {
        defaultValue: "\u0422\u0430\u043A\u043E\u0433\u043E \u0433\u043E\u0440\u043E\u0434\u0430 \u043D\u0435\u0442 \u0432 \u0441\u043F\u0438\u0441\u043A\u0435"
      })}</span> : shown.map(c_0 => <button key={c_0} type="button" role="option" aria-selected={c_0 === value} onClick={() => onChange(c_0)} className={cn("shrink-0 rounded-[8px] px-3 py-2.5 text-left text-[14px] leading-[19px] font-medium text-[#1F1F1F] outline-none transition-colors hover:bg-[#1F1F1F]/[0.04] focus-visible:bg-[#1F1F1F]/[0.04]", c_0 === value && "bg-[#1F1F1F]/[0.04]")}>{c_0}</button>);
    $[17] = loading;
    $[18] = onChange;
    $[19] = shown;
    $[20] = t;
    $[21] = value;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
    t10 = <div role="listbox" aria-label={t7} className={t8}>{t9}</div>;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t6) {
    t11 = <div className="flex flex-col gap-2.5">{t6}{t10}</div>;
    $[27] = t10;
    $[28] = t6;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  return t11;
}
function Field(t0) {
  const $ = _c(5);
  const {
    label,
    children
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <span className="text-[14px] leading-[18px] font-semibold text-[#1F1F1F]">{label}</span>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className="flex flex-col gap-1.5">{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}